import { Box, Typography } from "@mui/material";
import { get } from "lodash-es";
import {
	type Control,
	Controller,
	type DeepMap,
	type FieldError,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";

import { ErrorMessage } from "Forms/Components/ErrorMessage";
import {
	StyledFormControl,
	StyledFormInput,
	StyledFormLabelWrapper,
} from "Forms/Components/FormStyles";
import type { CodeListItem } from "Infrastructure/Models/ICodeListDto";
import { BlDropdown as DropdownInput } from "Shared/Inputs/BlDropdown";
import { StyledInputLabel } from "Shared/StyledComponents";

export type DropdownValueType = string | number | boolean;

export type BlFormDropdownProps<
	FormType extends FieldValues,
	U extends boolean | undefined,
> = {
	codeList: CodeListItem[];
	name: FieldPath<FormType>;
	formControlClass?: "long" | "medium" | "tiny" | string;
	label?: string;
	labelTooltip?: string;
	control: Control<FormType, object>;
	errors: DeepMap<any, FieldError>;
	disabled?: boolean;
	placeholder?: string;
	hasFilterStyle?: boolean;
	multiple?: U;
	labelEndAdornment?: React.JSX.Element;
	onChange?: U extends true
		? (value: DropdownValueType[] | null) => void
		: (value: DropdownValueType | null) => void;
};

export const BlFormDropdown = <T extends object, U extends boolean>({
	codeList,
	name,
	control,
	label,
	disabled,
	errors,
	formControlClass,
	placeholder,
	hasFilterStyle,
	multiple,
	labelEndAdornment,
	onChange,
}: BlFormDropdownProps<T, U>) => {
	const error: FieldError | undefined = get(errors, name);

	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { onChange: controllerOnChange, value, onBlur, name, ref },
			}) => {
				const isFilled =
					!!value &&
					(multiple
						? codeList.some((e) =>
								(value as []).some((v) => e.id === v || e.code === v),
							)
						: codeList.some((e) => e.id === value || e.code === value));

				const formValue = isFilled ? value : multiple ? [] : "";

				const onValueChange = (
					val: DropdownValueType | DropdownValueType[] | null,
				) => {
					controllerOnChange(val);
					if (onChange) {
						onChange(val as any);
					}
				};

				return (
					<StyledFormControl
						className={`bl-form-control ${formControlClass ?? ""} ${
							!label ? "no-label" : ""
						} ${hasFilterStyle ? "filter" : ""}`}
					>
						{!!label && (
							<StyledFormLabelWrapper
								$disabled={disabled ?? false}
								className="bl-form-control__label"
							>
								<StyledInputLabel htmlFor={name} $hasError={!!error}>
									<Typography>{label}</Typography>
									{!!labelEndAdornment && (
										<Box m={0.5}>{labelEndAdornment}</Box>
									)}
								</StyledInputLabel>
							</StyledFormLabelWrapper>
						)}
						<StyledFormInput className="bl-form-control__input">
							<DropdownInput
								value={formValue}
								onChange={onValueChange}
								onBlur={onBlur}
								disabled={disabled}
								hasError={!!error}
								name={name}
								placeholder={placeholder}
								options={codeList.map((x) => ({
									Name: x.name,
									Value: x.id ?? x.code,
								}))}
								hasFilterStyle={hasFilterStyle}
								multiple={multiple}
								inputRef={ref}
							/>
						</StyledFormInput>
						{((hasFilterStyle && error) || !hasFilterStyle) && (
							<ErrorMessage message={error?.message} />
						)}
					</StyledFormControl>
				);
			}}
		/>
	);
};
